import { type TvPlaylist } from '@setplex/tria-api'
import { useCallback, useState } from 'react'
import type { TvWithPrice } from '~/shared/interfaces'
import { type SearchParamsForPath } from '~/shared/interfaces/player/generic'

export const getSearchParams = (search: string) => {
  const sParams = new URLSearchParams(search)

  const categoryId = sParams.get('categoryId')
  const isTrailer = sParams.has('trailer')

  // Channels
  const channelId = sParams.get('channelId') // also catchup
  const channelNumber = Number(sParams.get('channelNumber'))
  const featured = sParams.has('featured')
  const favorite = sParams.has('favorite')
  const purchased = sParams.has('purchased')
  const recommended = sParams.has('recommended')
  const recentlyWatched = sParams.has('recentlyWatched')

  // Movies
  const vodIndex = Number(sParams.get('vodIndex'))
  const searchParamVodId = sParams.get('vodId')
  const vodId = searchParamVodId == null ? null : Number(searchParamVodId)

  // Tv Shows
  const tvShowId = Number(sParams.get('tvshowId'))
  const seasonId =
    sParams.has('seasonId') &&
    sParams.get('seasonId') !== 'null' &&
    sParams.get('seasonId') !== 'undefined'
      ? Number(sParams.get('seasonId'))
      : undefined
  const episodeId = Number(sParams.get('episodeId'))
  const showIndex = Number(sParams.get('showIndex'))

  // Catchups
  // channelId from channels
  const catchupId = Number(sParams.get('catchupId'))
  const programmeId = Number(sParams.get('programmeId'))

  //liveEvents
  const liveEventId = Number(sParams.get('liveEventId'))

  return {
    categoryId,
    channelId,
    channelNumber,
    featured,
    vodIndex,
    vodId,
    tvShowId,
    seasonId,
    episodeId,
    showIndex,
    isTrailer,
    catchupId,
    programmeId,
    liveEventId,
    favorite,
    purchased,
    recommended,
    recentlyWatched,
  }
}

type CurrentChannelInfoProps = Pick<TvPlaylist, 'channels'> & {
  searchParam: string
  recentlyWatchedChannels: TvWithPrice[]
}

export const getCurrentChannelInfo = ({
  searchParam,
  channels: playlistChannels,
  recentlyWatchedChannels,
}: CurrentChannelInfoProps) => {
  const {
    categoryId,
    featured,
    channelId,
    favorite,
    purchased,
    recentlyWatched,
    recommended,
  } = getSearchParams(searchParam)
  const channels = recentlyWatched ? recentlyWatchedChannels : playlistChannels

  if (!channels.length || !channelId || !categoryId) return null

  const currentIndex = channels.findIndex(
    (channel) => channel.id === Number(channelId)
  )
  const currentTvChannel = channels[currentIndex]
  const channelsCount = channels.length
  return {
    currentIndex,
    currentTvChannel,
    channelsCount,
    categoryId,
    featured,
    favorite,
    purchased,
    recentlyWatched,
    recommended,
  }
}

export const addSearchParamsForPath = ({
  params,
  path,
}: SearchParamsForPath): string => {
  const searchParams: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    // eslint-disable-next-line eqeqeq
    if (value != null) {
      searchParams[key] = value.toString()
    }
  }
  return `${path}?${new URLSearchParams(searchParams)}`
}

export const useResetToken = (reloadUrl: () => void) => {
  const [played, setPlayed] = useState(false)

  const onPlaying = useCallback(() => {
    if (!played) {
      setPlayed(true)
    }
  }, [played])

  const onTokenExpired = useCallback(() => {
    if (played) {
      reloadUrl()
      setPlayed(false)
    }
  }, [played, reloadUrl])

  return {
    onPlaying,
    onTokenExpired,
  }
}
